// Little pop-up notes ("Harsh braking!", "Saved"). One stack per app.
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

type Toast = { id: number; text: string; tone: 'sky' | 'sun' | 'leaf' | 'coral' | 'danger'; icon?: IconName };
type ToastApi = { push: (text: string, opts?: { tone?: Toast['tone']; icon?: IconName; ms?: number }) => void };

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const push = useCallback<ToastApi['push']>((text, opts) => {
    const id = next.current++;
    setToasts((t) => [...t.slice(-4), { id, text, tone: opts?.tone ?? 'sky', icon: opts?.icon }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), opts?.ms ?? 3500);
  }, []);
  const api = useMemo(() => ({ push }), [push]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast tone-${t.tone}`}>
            {t.icon ? <Icon name={t.icon} size={18} /> : null}
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToasts(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToasts outside ToastProvider');
  return ctx;
}
